import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { useNavigate, useSearchParams } from "react-router-dom";

import { formatDob } from "../../../../utils/dateUtils";

interface RelatedProfilesProps {
  patient?: PatientMessage;
  currentPatientId: string;
}

const getFullName = (profile: PatientMessage): string => {
  const middle = profile.wieldyMiddleName ? ` ${profile.wieldyMiddleName}` : "";
  return `${profile.wieldyFirstName || ""}${middle} ${profile.wieldyLastName || ""}`.trim();
};

export default function RelatedProfiles({
  patient,
  currentPatientId,
}: RelatedProfilesProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const filteredProfiles =
    patient?.profiles?.filter(
      (profile) => profile.wieldyId !== currentPatientId,
    ) || [];

  const handleProfileClick = (wieldyId: string) => {
    const currentSearch = searchParams.toString();
    const path = currentSearch
      ? `/patients/${wieldyId}?${currentSearch}`
      : `/patients/${wieldyId}`;
    navigate(path);
  };

  return (
    <Paper
      sx={{
        padding: theme.spacing(2),
        paddingRight: theme.spacing(17),
        borderRadius: "8px 8px 8px 8px",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
      variant="outlined"
    >
      <Typography
        variant="h6"
        sx={{ fontWeight: 600, marginBottom: theme.spacing(2) }}
      >
        Related Patient Profiles
      </Typography>

      {filteredProfiles.length === 0 ? (
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            textAlign: "center",
            padding: theme.spacing(2),
          }}
        >
          No Profiles Found
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {filteredProfiles.map((profile, index) => (
            <Box key={profile.wieldyId}>
              {index > 0 && <Divider sx={{ marginBottom: 1.5 }} />}
              <Paper
                sx={{
                  padding: theme.spacing(2),
                  cursor: "pointer",
                  borderRadius: 1,
                  transition: "background-color 0.2s ease",
                  "&:hover": {
                    backgroundColor: theme.palette.grey[50],
                  },
                  "&:focus": {
                    outline: `2px solid ${theme.palette.primary.main}`,
                    outlineOffset: "2px",
                  },
                }}
                variant="outlined"
                onClick={() => handleProfileClick(profile.wieldyId)}
                tabIndex={0}
                role="button"
                aria-label={`View profile for ${getFullName(profile)}`}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleProfileClick(profile.wieldyId);
                  }
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 500, marginBottom: 0.5 }}
                >
                  {getFullName(profile) || "Unknown Name"}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.secondary }}
                >
                  DOB: {profile.dob ? formatDob(profile.dob) : "N/A"}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.secondary }}
                >
                  Source: {profile.source || "N/A"}
                </Typography>
              </Paper>
            </Box>
          ))}
        </Box>
      )}
    </Paper>
  );
}
